import { useEffect, useState } from 'react';
import {
  Typography, Button, TextField, MenuItem, Table, TableHead, TableRow, TableCell,
  TableBody, Paper, Chip, Stack, Grid, Card, CardContent, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Pagination, TableContainer,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import api from '../services/api';

// OCDS contract status in plain words for the public.
const CONTRACT_STATUS_LABEL = {
  PENDING: 'Not started',
  ACTIVE: 'In progress',
  TERMINATED: 'Completed',
  CANCELLED: 'Cancelled',
};

function StatCard({ label, value }) {
  return (
    <Card><CardContent>
      <Typography variant="h4" color="primary">{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </CardContent></Card>
  );
}

export default function PublicPortal() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState([]);
  const [closed, setClosed] = useState([]);
  const [closedPage, setClosedPage] = useState(1);
  const [closedTotal, setClosedTotal] = useState(0);
  const [awards, setAwards] = useState([]);
  const [awardPage, setAwardPage] = useState(1);
  const [awardTotal, setAwardTotal] = useState(0);
  const [summary, setSummary] = useState(null);

  const AWARDS_PER_PAGE = 20;
  const loadClosed = async (toPage) => {
    try {
      const r = await api.get('/public/tenders/closed', { params: { page: toPage, pageSize: AWARDS_PER_PAGE } });
      setClosed(r.data.data.items || r.data.data);
      setClosedTotal(r.data.meta?.total || 0);
      setClosedPage(toPage);
    } catch { /* leave the list as-is */ }
  };
  const loadAwards = async (toPage) => {
    try {
      const r = await api.get('/public/awards', { params: { page: toPage, pageSize: AWARDS_PER_PAGE } });
      setAwards(Array.isArray(r.data.data) ? r.data.data : (r.data.data.items || []));
      setAwardTotal(r.data.meta?.total || 0);
      setAwardPage(toPage);
    } catch { /* leave the list as-is */ }
  };
  const [mdas, setMdas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ mda: '', category: '', search: '' });

  const loadOpen = async () => {
    const params = {};
    if (filters.mda) params.mda = filters.mda;
    if (filters.category) params.category = filters.category;
    if (filters.search) params.search = filters.search;
    const { data } = await api.get('/public/tenders/open', { params });
    setOpen(data.data.items || data.data);
  };

  useEffect(() => {
    api.get('/public/mdas').then(r => setMdas(r.data.data)).catch(() => {});
    api.get('/public/categories').then(r => setCategories(r.data.data)).catch(() => {});
    api.get('/public/stats').then(r => setStats(r.data.data)).catch(() => {});
    loadClosed(1);
    loadAwards(1);
    loadOpen().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const TenderTable = ({ rows, closedView }) => (
    <Paper>
      <TableContainer>
      <Table sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell>Reference</TableCell><TableCell>Title</TableCell><TableCell>MDA</TableCell>
            {/* The cell below renders submissionDeadline in both views, so the
                header says Deadline in both. */}
            <TableCell>Category</TableCell><TableCell>Deadline</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={6}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nothing to show here yet.</Typography></TableCell></TableRow>
          )}
          {rows.map((t) => (
            <TableRow key={t.id} hover>
              <TableCell>{t.reference}</TableCell>
              <TableCell>{t.title}</TableCell>
              <TableCell>{t.mda?.name}</TableCell>
              <TableCell><Chip size="small" label={t.category} /></TableCell>
              <TableCell>{t.submissionDeadline ? new Date(t.submissionDeadline).toLocaleDateString('en-GB') : '-'}</TableCell>
              <TableCell align="right"><Button size="small" onClick={() => navigate(`/portal/tenders/${t.id}`)}>View details</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <PublicLayout>
      <Typography variant="h4" gutterBottom>Public Procurement</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Browse public procurement opportunities and records across all ministries, departments and agencies.
      </Typography>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}><StatCard label="Open tenders" value={stats.openTenders} /></Grid>
          <Grid item xs={12} sm={4}><StatCard label="Contracts awarded" value={stats.contractsAwarded} /></Grid>
          <Grid item xs={12} sm={4}><StatCard label="Participating MDAs" value={stats.tendersByMdaCount} /></Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Open Tenders" />
          <Tab label="Closed Tenders" />
          <Tab label="Awarded Contracts" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField select label="MDA" value={filters.mda} onChange={(e) => setFilters({ ...filters, mda: e.target.value })} sx={{ minWidth: 200 }}>
                <MenuItem value="">All</MenuItem>
                {mdas.map((m) => <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>)}
              </TextField>
              <TextField select label="Category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} sx={{ minWidth: 200 }}>
                <MenuItem value="">All</MenuItem>
                {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField label="Search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} fullWidth />
              <Button variant="contained" onClick={loadOpen}>Apply</Button>
            </Stack>
          </Paper>
          <TenderTable rows={open} />
        </>
      )}

      {tab === 1 && <TenderTable rows={closed} closedView />}
      {tab === 1 && closedTotal > AWARDS_PER_PAGE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(closedTotal / AWARDS_PER_PAGE)}
            page={closedPage}
            onChange={(_, p) => loadClosed(p)}
            color="primary"
          />
        </Box>
      )}

      {tab === 2 && (
        <Paper>
          <TableContainer>
          <Table sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell><TableCell>Project</TableCell><TableCell>Vendor</TableCell>
                <TableCell>Amount</TableCell><TableCell>Awarded</TableCell><TableCell align="right">Contract Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {awards.length === 0 && (
                <TableRow><TableCell colSpan={6}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No contract awards published yet.</Typography></TableCell></TableRow>
              )}
              {awards.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.tender?.reference}</TableCell>
                  <TableCell>{a.tender?.title}</TableCell>
                  <TableCell>{a.vendorName}</TableCell>
                  <TableCell>{Number(a.contractAmount).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' })}</TableCell>
                  <TableCell>{a.awardDate ? new Date(a.awardDate).toLocaleDateString('en-GB') : '-'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setSummary(a)}>View Contract Summary</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </Paper>
      )}
      {tab === 2 && awardTotal > AWARDS_PER_PAGE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(awardTotal / AWARDS_PER_PAGE)}
            page={awardPage}
            onChange={(_, p) => loadAwards(p)}
            color="primary"
          />
        </Box>
      )}
      <Dialog open={!!summary} onClose={() => setSummary(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Contract Award Summary</DialogTitle>
        <DialogContent dividers>
          {summary && (
            <Stack spacing={1.5}>
              <Box><Typography variant="caption" color="text.secondary">Project</Typography><Typography>{summary.tender?.title}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Reference</Typography><Typography>{summary.tender?.reference}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">MDA</Typography><Typography>{summary.tender?.mda?.name}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Winning Vendor</Typography><Typography>{summary.vendorName}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Contract Amount</Typography><Typography>{Number(summary.contractAmount).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' })}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Award Date</Typography><Typography>{summary.awardDate ? new Date(summary.awardDate).toLocaleDateString('en-GB') : '-'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Contract Period</Typography><Typography>{summary.contractStart ? new Date(summary.contractStart).toLocaleDateString('en-GB') : '-'} to {summary.contractEnd ? new Date(summary.contractEnd).toLocaleDateString('en-GB') : '-'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Contract Status</Typography><Typography>{CONTRACT_STATUS_LABEL[summary.contractStatus] || '-'}</Typography></Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>

          <Button onClick={() => setSummary(null)}>Close</Button>
        </DialogActions>
      </Dialog>

    </PublicLayout>
  );
}











